import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Grid3x3, MousePointerClick, X } from 'lucide-react';
import CanFrameEditor from './CanFrameEditor';
import BitGrid from './BitGrid';
import SignalList from './SignalList';
import SignalFormDialog from './SignalFormDialog';
import SignalResults from './SignalResults';
import { getSignalBitPositions, cellKey, computeSelectionRange } from '../../../lib/canEndianness/bitMapping';
import { findOverlappingSignalIds, fitsWithinDlc } from '../../../lib/canEndianness/validation';
import { decodeSignal, computeRawFromPhysical, writeSignalRawIntoBytes } from '../../../lib/canEndianness/decode';
import {
  DEFAULT_CAN_ID_HEX,
  DEFAULT_DLC,
  DEFAULT_DATA_HEX,
  BLANK_DATA_HEX,
  makeDefaultSignals,
  makeBlankSignal,
} from '../../../lib/canEndianness/defaults';

const toByteNumbers = (hexBytes) => hexBytes.map((h) => parseInt(h, 16) || 0);
const toHexBytes = (numberBytes) => numberBytes.map((n) => n.toString(16).toUpperCase().padStart(2, '0'));

function findDefaultSlot(dlc, occupiedCells) {
  for (const tryLength of [8, 4, 2, 1]) {
    for (let startBit = 0; startBit <= dlc * 8 - tryLength; startBit++) {
      const positions = getSignalBitPositions(startBit, tryLength, 'intel');
      if (positions.every((p) => !occupiedCells.has(cellKey(p.byteIndex, p.bitInByte)))) {
        return { startBit, length: tryLength };
      }
    }
  }
  return null;
}

const CanEndiannessVisualizer = () => {
  const [canIdHex, setCanIdHex] = useState(DEFAULT_CAN_ID_HEX);
  const [dlc, setDlc] = useState(DEFAULT_DLC);
  const [dataBytes, setDataBytes] = useState(() => toByteNumbers(DEFAULT_DATA_HEX));
  const [signals, setSignals] = useState(() => makeDefaultSignals());

  const [selectedSignalId, setSelectedSignalId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [newSignalByteOrder, setNewSignalByteOrder] = useState('intel');
  const [pendingAnchor, setPendingAnchor] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [dialogSignal, setDialogSignal] = useState(null);

  const dataHexBytes = useMemo(() => toHexBytes(dataBytes), [dataBytes]);
  const canIdDecimal = useMemo(() => parseInt(canIdHex, 16) || 0, [canIdHex]);

  const cellOwnership = useMemo(() => {
    const map = new Map();
    signals.forEach((signal) => {
      const positions = getSignalBitPositions(signal.startBit, signal.length, signal.byteOrder);
      positions.forEach((pos, idx) => {
        const key = cellKey(pos.byteIndex, pos.bitInByte);
        const arr = map.get(key) || [];
        arr.push({ signalId: signal.id, positionIndex: idx });
        map.set(key, arr);
      });
    });
    return map;
  }, [signals]);

  const overlappingSignalIds = useMemo(() => findOverlappingSignalIds(signals), [signals]);
  const outOfRangeSignalIds = useMemo(
    () => new Set(signals.filter((s) => !fitsWithinDlc(s, dlc)).map((s) => s.id)),
    [signals, dlc]
  );

  const defaultSlot = useMemo(() => findDefaultSlot(dlc, new Set(cellOwnership.keys())), [dlc, cellOwnership]);

  const handleByteChange = (index, hexStr) => {
    const value = parseInt(hexStr, 16);
    setDataBytes((prev) => prev.map((b, i) => (i === index ? (Number.isNaN(value) ? 0 : value) : b)));
  };

  const handleReset = () => {
    setCanIdHex('000');
    setDlc(8);
    setDataBytes(toByteNumbers(BLANK_DATA_HEX));
    setSignals([]);
    setSelectedSignalId(null);
    setSelectMode(false);
    setPendingAnchor(null);
  };

  const handleLoadExample = () => {
    setCanIdHex(DEFAULT_CAN_ID_HEX);
    setDlc(DEFAULT_DLC);
    setDataBytes(toByteNumbers(DEFAULT_DATA_HEX));
    setSignals(makeDefaultSignals());
    setSelectedSignalId(null);
    setSelectMode(false);
    setPendingAnchor(null);
  };

  const handleToggleSelectMode = () => {
    setSelectMode((v) => !v);
    setPendingAnchor(null);
  };

  const handleCellClick = (byteIndex, bitInByte, ownerSignalId) => {
    if (selectMode) {
      if (!pendingAnchor) {
        setPendingAnchor({ byteIndex, bitInByte });
        return;
      }
      const range = computeSelectionRange(pendingAnchor, { byteIndex, bitInByte }, newSignalByteOrder);
      setPendingAnchor(null);
      setSelectMode(false);
      setDialogMode('add');
      setDialogSignal(makeBlankSignal({ startBit: range.startBit, length: range.length, byteOrder: newSignalByteOrder }));
      setDialogOpen(true);
      return;
    }
    if (ownerSignalId) {
      setSelectedSignalId((prev) => (prev === ownerSignalId ? null : ownerSignalId));
    }
  };

  const openAddDialog = () => {
    if (!defaultSlot) return;
    setDialogMode('add');
    setDialogSignal(makeBlankSignal({ startBit: defaultSlot.startBit, length: defaultSlot.length }));
    setDialogOpen(true);
  };

  const openEditDialog = (id) => {
    const signal = signals.find((s) => s.id === id);
    if (!signal) return;
    setDialogMode('edit');
    setDialogSignal({ ...signal });
    setDialogOpen(true);
  };

  const handleDialogSave = (signal) => {
    if (dialogMode === 'edit') {
      // Re-encode so the physical value the user was looking at (e.g. 100 km/h)
      // survives edits to byte order / start bit / length / factor / offset —
      // otherwise the same raw bytes would just get reinterpreted and the
      // displayed value would silently jump.
      const previousSignal = signals.find((s) => s.id === signal.id);
      if (previousSignal) {
        const { physical } = decodeSignal(dataBytes, previousSignal);
        const { rawUnsigned } = computeRawFromPhysical(signal, physical);
        setDataBytes((prev) => writeSignalRawIntoBytes(prev, signal, rawUnsigned));
      }
    }
    setSignals((prev) => (dialogMode === 'edit' ? prev.map((s) => (s.id === signal.id ? signal : s)) : [...prev, signal]));
    setSelectedSignalId(signal.id);
    setDialogOpen(false);
    toast.success(dialogMode === 'edit' ? `${signal.name} updated.` : `${signal.name} added.`);
  };

  const handleDeleteSignal = (id) => {
    const signal = signals.find((s) => s.id === id);
    setSignals((prev) => prev.filter((s) => s.id !== id));
    if (selectedSignalId === id) setSelectedSignalId(null);
    if (signal) toast.success(`${signal.name || 'Signal'} removed.`);
  };

  const handlePhysicalEdit = (signalId, physicalValue) => {
    const signal = signals.find((s) => s.id === signalId);
    if (!signal) return;
    const { rawUnsigned } = computeRawFromPhysical(signal, physicalValue);
    setDataBytes((prev) => writeSignalRawIntoBytes(prev, signal, rawUnsigned));
  };

  return (
    <div className="space-y-6">
      <CanFrameEditor
        canIdHex={canIdHex}
        onCanIdChange={setCanIdHex}
        dlc={dlc}
        onDlcChange={setDlc}
        dataBytes={dataHexBytes}
        onByteChange={handleByteChange}
        onReset={handleReset}
        onLoadExample={handleLoadExample}
      />

      <SignalList
        signals={signals}
        selectedSignalId={selectedSignalId}
        overlappingSignalIds={overlappingSignalIds}
        outOfRangeSignalIds={outOfRangeSignalIds}
        onSelect={setSelectedSignalId}
        onEdit={openEditDialog}
        onDelete={handleDeleteSignal}
        onAdd={openAddDialog}
        addDisabled={!defaultSlot}
      />

      <SignalResults
        signals={signals}
        dataBytes={dataBytes}
        canIdDecimal={canIdDecimal}
        dlc={dlc}
        selectedSignalId={selectedSignalId}
        overlappingSignalIds={overlappingSignalIds}
        outOfRangeSignalIds={outOfRangeSignalIds}
        onSelect={setSelectedSignalId}
        onPhysicalEdit={handlePhysicalEdit}
      />

      <Card className="technical-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
              <Grid3x3 className="h-5 w-5 text-autoxium-blue" />
              Bit Visualization
            </CardTitle>
            <CardDescription>Click a signal's bits to highlight it, or select two bits to create a new signal.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {selectMode && (
              <Select value={newSignalByteOrder} onValueChange={setNewSignalByteOrder}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="can-endianness-select-byteorder">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intel">Intel (Little Endian)</SelectItem>
                  <SelectItem value="motorola">Motorola (Big Endian)</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              type="button"
              variant={selectMode ? 'default' : 'outline'}
              size="sm"
              className={selectMode ? 'btn-premium' : ''}
              onClick={handleToggleSelectMode}
              data-testid="can-endianness-toggle-select-mode-btn"
            >
              {selectMode ? <X className="h-3.5 w-3.5" /> : <MousePointerClick className="h-3.5 w-3.5" />}
              {selectMode ? 'Cancel Selection' : 'Select Bits'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <BitGrid
            dlc={dlc}
            dataBytes={dataBytes}
            signals={signals}
            cellOwnership={cellOwnership}
            selectedSignalId={selectedSignalId}
            selectMode={selectMode}
            pendingAnchor={pendingAnchor}
            onCellClick={handleCellClick}
          />
        </CardContent>
      </Card>

      <SignalFormDialog
        open={dialogOpen}
        mode={dialogMode}
        initialSignal={dialogSignal}
        dlc={dlc}
        otherSignals={signals}
        onSave={handleDialogSave}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default CanEndiannessVisualizer;
